import { useState } from 'react';
import {
  updateUserAttributes,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
} from 'aws-amplify/auth';
import { Heading } from '@aws-amplify/ui-react';
import { UserProfile } from '../types';

interface UserProfileManagerProps {
  userProfile: UserProfile;
}

export function UserProfileManager({ userProfile }: UserProfileManagerProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [message, setMessage] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const handleSendCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage('Sending verification code...');
    try {
      const formattedPhoneNumber = phoneNumber.startsWith('+')
        ? phoneNumber
        : `+${phoneNumber}`;
      await updateUserAttributes({
        userAttributes: { phone_number: formattedPhoneNumber },
      });
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
      setIsCodeSent(true);
      setMessage('Verification code sent to your phone.');
    } catch (error) {
      console.error('Error sending MFA code:', error);
      setMessage('Failed to send code. Please check number format.');
    }
  };

  const handleVerifyCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage('Verifying code...');
    try {
      await confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: verificationCode,
      });
      setMessage('Phone number updated successfully!');
      setIsEditingPhone(false);
      setIsCodeSent(false);
      setPhoneNumber('');
      setVerificationCode('');
    } catch (error) {
      console.error('Error verifying MFA code:', error);
      setMessage('Failed to verify code.');
    }
  };

  return (
    <div
      style={{ border: '1px solid #ccc', padding: '15px', marginTop: '20px' }}
    >
      <Heading level={4}>My Profile</Heading>
      <p>
        <strong>Name:</strong> {userProfile.firstName} {userProfile.lastName}
      </p>
      <p>
        <strong>Company:</strong> {userProfile?.company?.name}
      </p>

      <hr style={{ margin: '15px 0' }} />

      {!isEditingPhone ? (
        <button onClick={() => setIsEditingPhone(true)}>
          Update Mobile Number
        </button>
      ) : (
        <div>
          <Heading level={5}>Update Mobile Number</Heading>
          {!isCodeSent ? (
            <form
              onSubmit={handleSendCode}
              style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              <label htmlFor='phone_number'>
                New Mobile Number (e.g., +447123456789)
              </label>
              <input
                id='phone_number'
                type='tel'
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
              <button type='submit'>Send Code</button>
              <button type='button' onClick={() => setIsEditingPhone(false)}>
                Cancel
              </button>
            </form>
          ) : (
            <form
              onSubmit={handleVerifyCode}
              style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
            >
              <label htmlFor='verification_code'>Verification Code</label>
              <input
                id='verification_code'
                type='text'
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
              />
              <button type='submit'>Verify & Save</button>
              <button type='button' onClick={() => setIsCodeSent(false)}>
                Back
              </button>
            </form>
          )}
        </div>
      )}

      {message && (
        <p>
          <em>{message}</em>
        </p>
      )}

      <hr style={{ margin: '15px 0' }} />

      <p>
        To change other details like your name or company, please contact
        support.
      </p>
      <a href='mailto:support@aslan.io'>Email Support</a>
    </div>
  );
}
